import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  useTheme,
  Paper,
  Divider,
  Avatar,
  Chip,
  Grid,
  useMediaQuery,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AttachFile,
  Send,
  AutoAwesome,
  Upload,
  Psychology,
  QuestionAnswer,
  Lightbulb,
  History,
  Close,
  Menu,
  Mic,
  Mood,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Add,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp, limit, Timestamp, doc, updateDoc, getDoc, writeBatch, setDoc } from 'firebase/firestore';
import { openai } from '../../services/openai';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import AddToJournalButton from '../analytics/AddToJournalButton';
import AIChatbot from '../analytics/AIChatbot';
import LoadingMessage from '../LoadingMessage';

const FOLLOW_UP_QUESTIONS = [
  "Can you explain why the correct answer is right in simpler terms?",
  "What makes the wrong answers incorrect?",
  "What's the key principle being tested in this question?",
];

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString();
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }
  return new Date(timestamp).toLocaleDateString();
};

const AnalysisSidebar = ({ 
  analysisHistory, 
  handleSelectAnalysis, 
  selectedAnalysis, 
  handleImageUpload, 
  getRootProps, 
  getInputProps, 
  isDragActive,
  onNewAnalysis,
  isMobileView,
  isOpen,
  onClose
}) => {
  const theme = useTheme();

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        width: { xs: '100%', sm: '90%', md: '320px' },
        maxWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.mode === 'dark'
          ? 'rgba(15, 23, 42, 0.8)'
          : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.5)'
          : '0 4px 12px rgba(0,0,0,0.1)',
        borderRight: { xs: 'none', md: '1px solid' },
        borderColor: theme.palette.divider,
        borderTopRightRadius: { xs: 0, md: 12 },
        borderBottomRightRadius: { xs: 0, md: 12 },
      }}
    >
      {/* Mobile Header */}
      {isMobileView && (
        <Box sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Previous Analyses
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      )}

      {/* New Analysis Button */}
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Button
          variant="contained"
          onClick={onNewAnalysis}
          startIcon={<Upload />}
          fullWidth
          sx={{
            py: 1.5,
            borderRadius: 2,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)'
              : 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)'
                : 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          New Analysis
        </Button>
      </Box>

      <Divider sx={{ my: { xs: 1, md: 2 } }} />

      {/* Previous Questions */}
      <Box sx={{
        flex: 1,
        px: { xs: 1.5, md: 2 },
        overflowY: 'auto',
        mb: 2,
      }}>
        <Typography variant="subtitle2" sx={{ 
          mb: 2,
          color: theme.palette.text.secondary,
          fontWeight: 600,
          letterSpacing: '0.05em',
        }}>
          PREVIOUS QUESTIONS
        </Typography>
        {analysisHistory.map((item, index) => (
          <Paper
            key={index}
            onClick={() => {
              handleSelectAnalysis(item);
              if (isMobileView) {
                onClose();
              }
            }}
            sx={{ 
              p: 2,
              mb: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              border: '1px solid',
              borderColor: selectedAnalysis?.id === item.id
                ? theme.palette.primary.main
                : theme.palette.divider,
              bgcolor: selectedAnalysis?.id === item.id
                ? theme.palette.mode === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : 'rgba(96, 165, 250, 0.2)'
                : 'transparent',
              boxShadow: selectedAnalysis?.id === item.id ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                transform: 'translateY(-2px)',
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : 'rgba(96, 165, 250, 0.2)',
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Question #{analysisHistory.length - index}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.analysis.questionType}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(item.timestamp)}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );

  return isMobileView ? (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '320px',
          background: theme.palette.mode === 'dark'
            ? 'rgba(15, 23, 42, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.5)'
            : '0 4px 12px rgba(0,0,0,0.1)',
        }
      }}
    >
      {sidebarContent}
    </Drawer>
  ) : (
    sidebarContent
  );
};

const UploadArea = ({ getRootProps, getInputProps, isDragActive, handlePaste }) => {
  const theme = useTheme();

  return (
    <Paper
      {...getRootProps()}
      onPaste={handlePaste}
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 2,
        border: '2px dashed',
        borderColor: isDragActive ? theme.palette.primary.main : theme.palette.divider,
        bgcolor: theme.palette.background.default,
        cursor: 'pointer',
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        {isDragActive ? 'Drop the files here...' : 'Drag & drop or click to upload'}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Supported formats: JPEG, JPG, PNG
      </Typography>
    </Paper>
  );
};

const AnalysisResults = ({ result, uploadedImage, selectedAnalysis }) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Question Image */}
      {uploadedImage && (
        <Paper sx={{ overflow: 'hidden', borderRadius: 2, mb: 3 }}>
          <img
            src={uploadedImage}
            alt="Question"
            style={{
              width: '100%',
              maxHeight: '300px',
              objectFit: 'contain',
            }}
          />
        </Paper>
      )}

      {/* Analysis Results */}
      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: theme.palette.background.paper, boxShadow: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesome sx={{ fontSize: 28, color: theme.palette.primary.main, mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Analysis
          </Typography>
          <Box sx={{ flex: 1 }} />
          <AddToJournalButton
            questionData={{
              imageUrl: uploadedImage,
              questionType: result.questionType,
              summary: result.summary,
              answerAnalysis: result.answerAnalysis,
              aiAnalysisId: selectedAnalysis?.id,
            }}
            selectedAnalysisId={selectedAnalysis?.id}
            chatHistory={selectedAnalysis?.chatHistory || []}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Question Type
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{result.questionType}</Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Summary
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{result.summary}</Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Answer Analysis
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '1rem',
                lineHeight: 1.6,
              }}
            >
              {result.answerAnalysis}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
};

const ChatInterface = ({ questionContext, initialChatHistory, onChatUpdate }) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderRadius: 1,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <QuestionAnswer sx={{ 
          fontSize: { xs: 24, sm: 28 }, 
          color: 'primary.main',
        }} />
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
          }}
        >
          Chat with AI Tutor
        </Typography>
      </Box>

      <Box sx={{ flex: 1, position: 'relative' }}>
        <AIChatbot
          key={questionContext.currentAnalysisId}
          questionContext={questionContext}
          initialChatHistory={initialChatHistory}
          onChatUpdate={onChatUpdate}
        />
      </Box>
    </Box>
  );
};

const AIAnalysis = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [input, setInput] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load analysis history
  useEffect(() => {
    const loadHistory = async () => {
      if (!currentUser) return;
      
      try {
        const analysesRef = collection(db, 'analyses');
        const q = query(
          analysesRef,
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(20)
        );
        
        const querySnapshot = await getDocs(q);
        const history = await Promise.all(querySnapshot.docs.map(async doc => {
          const analysisRef = doc.ref;
          const analysisDoc = await getDoc(analysisRef);
          return {
            id: doc.id,
            ...doc.data(),
            chatHistory: analysisDoc.data()?.chatHistory || []
          };
        }));
        
        setAnalysisHistory(history);
      } catch (err) {
        console.error('Error loading analysis history:', err);
      }
    };

    loadHistory();
  }, [currentUser]);

  // Function to update Firestore
  const updateAnalysisInFirestore = async (analysisId, data) => {
    if (!analysisId) return;
    try {
      const analysisRef = doc(db, 'analyses', analysisId);
      await updateDoc(analysisRef, data);
    } catch (err) {
      console.error('Error updating analysis:', err);
      throw err;
    }
  };

  // Switching between analyses and updating chat history
  const handleSelectAnalysis = async (analysis) => {
    try {
      if (selectedAnalysis?.id && chatHistory.length > 0) {
        await updateAnalysisInFirestore(selectedAnalysis.id, { chatHistory });
        setAnalysisHistory(prev => 
          prev.map(item => 
            item.id === selectedAnalysis.id 
              ? { ...item, chatHistory } 
              : item
          )
        );
      }
      const analysisRef = doc(db, 'analyses', analysis.id);
      const analysisDoc = await getDoc(analysisRef);
      const latestChatHistory = analysisDoc.data()?.chatHistory || [];
      const updatedAnalysis = {
        ...analysis,
        chatHistory: latestChatHistory
      };
      setSelectedAnalysis(updatedAnalysis);
      setUploadedImage(analysis.imageUrl);
      setResult(analysis.analysis);
      setChatHistory(latestChatHistory);
      setAnalysisHistory(prev => 
        prev.map(item => 
          item.id === analysis.id ? updatedAnalysis : item
        )
      );
    } catch (err) {
      console.error('Error switching analysis:', err);
      setError('Failed to load chat history. Please try again.');
    }
  };

  // Generate a hash for the image content
  const generateImageHash = async (base64Image) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(base64Image);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Handle cached analysis
  const handleCachedAnalysis = async (existingAnalysis, imageHash, base64Image) => {
    try {
      if (!existingAnalysis || !existingAnalysis.id) {
        throw new Error('Invalid analysis data');
      }

      const analysisRef = doc(db, 'analyses', existingAnalysis.id);
      const analysisDoc = await getDoc(analysisRef);
      
      if (!analysisDoc.exists()) {
        throw new Error('Analysis document not found');
      }

      const analysisData = analysisDoc.data();
      const latestChatHistory = analysisData?.chatHistory || [];
      
      const cachedAnalysis = {
        id: existingAnalysis.id,
        imageUrl: base64Image,
        analysis: existingAnalysis.analysis,
        timestamp: existingAnalysis.timestamp || Timestamp.now(),
        imageHash: imageHash,
        chatHistory: latestChatHistory,
        userId: existingAnalysis.userId
      };
      
      setResult(existingAnalysis.analysis);
      setChatHistory(latestChatHistory);
      setSelectedAnalysis(cachedAnalysis);
      
      setAnalysisHistory(prev => {
        const existingIndex = prev.findIndex(item => item.id === existingAnalysis.id);
        if (existingIndex !== -1) {
          const newHistory = [...prev];
          newHistory[existingIndex] = cachedAnalysis;
          return newHistory;
        }
        return [cachedAnalysis, ...prev];
      });
    } catch (err) {
      console.error('Error loading cached analysis:', err);
      throw new Error('Failed to load previous analysis');
    }
  };

  // Process image upload and analysis
  const handleImageUpload = async (file) => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const reader = new FileReader();
      
      reader.onerror = () => {
        setError('Failed to read the image file. Please try again.');
        setLoading(false);
      };

      reader.onload = async (e) => {
        try {
          const base64Image = e.target.result;
          setUploadedImage(base64Image);
          const imageHash = await generateImageHash(base64Image);
          
          // Check for existing analysis
          const analysesRef = collection(db, 'analyses');
          const q = query(
            analysesRef,
            where('userId', '==', currentUser.uid),
            where('imageHash', '==', imageHash)
          );

          const querySnapshot = await getDocs(q);
          
          // Check shared cache
          const sharedCacheRef = collection(db, 'sharedAnalysisCache');
          const sharedQuery = query(
            sharedCacheRef,
            where('imageHash', '==', imageHash)
          );
          const sharedCacheSnapshot = await getDocs(sharedQuery);

          // If existing analysis found
          if (!querySnapshot.empty) {
            const existingDoc = querySnapshot.docs[0];
            const existingData = existingDoc.data();
            await handleCachedAnalysis({
              id: existingDoc.id,
              ...existingData
            }, imageHash, base64Image);
            setLoading(false);
            return;
          }

          // If shared cache found
          if (!sharedCacheSnapshot.empty) {
            const cacheDoc = sharedCacheSnapshot.docs[0];
            const cacheData = cacheDoc.data();
            
            // Create new analysis from cache
            const newAnalysisRef = doc(collection(db, 'analyses'));
            const newAnalysisData = {
              userId: currentUser.uid,
              imageHash: imageHash,
              imageUrl: base64Image,
              analysis: cacheData.analysis,
              timestamp: serverTimestamp(),
              chatHistory: []
            };
            
            await setDoc(newAnalysisRef, newAnalysisData);
            
            const newAnalysis = {
              id: newAnalysisRef.id,
              ...newAnalysisData,
              timestamp: new Date()
            };
            
            setResult(cacheData.analysis);
            setSelectedAnalysis(newAnalysis);
            setAnalysisHistory(prev => [newAnalysis, ...prev]);
            setLoading(false);
            return;
          }

          // If no existing analysis, proceed with new analysis
          console.log('Starting text extraction...');
          const textExtractionResponse = await openai.chat.completions.create({
            model: "gpt-4o-mini",
            messages: [
              {
                role: "user",
                content: [
                  { type: "text", text: "Extract the text from this question image. Return only the extracted text, nothing else." },
                  { type: "image_url", image_url: { url: base64Image } }
                ]
              }
            ],
            max_tokens: 1500
          });
          
          const extractedText = textExtractionResponse.choices[0].message.content;

          console.log('Starting question analysis...');
          const analysisResponse = await openai.chat.completions.create({
            model: "o3-mini",
            messages: [
              {
                role: "user",
                content: `Identify the question type. Summarize the stimulus (the passage) clearly and concisely, focusing on key details and logical structure.
                Evaluate all answer choices systematically, explaining why each is correct or incorrect, and select the best answer.
                After selecting the best answer, explain why each eliminated option cannot be correct using evidence from the passage.
                Double-check your conclusion against the argument to ensure it aligns logically with the passage: Don't make the generic explanation. Explain as if you are a natural human tutor.

T: [Question type in one line]
S: [Core argument in 2 lines max]

A:
✓[Letter]) [2-3 sentences explaining why correct]
✗[Letter]) [1-2 sentences explaining why wrong]
✗[Letter]) [1-2 sentences explaining why wrong]
✗[Letter]) [1-2 sentences explaining why wrong]
✗[Letter]) [1-2 sentences explaining why wrong]

Question:
${extractedText}`
              }
            ],
            reasoning_effort: "high"
          });

          const analysis = analysisResponse.choices[0].message.content;
          const tIndex = analysis.indexOf('T:');
          const sIndex = analysis.indexOf('S:');
          const aIndex = analysis.indexOf('A:');
          const formattedAnalysis = {
            questionType: tIndex !== -1 && sIndex !== -1 
              ? analysis.substring(tIndex + 2, sIndex).trim() 
              : 'Question type not provided',
            summary: sIndex !== -1 && aIndex !== -1 
              ? analysis.substring(sIndex + 2, aIndex).trim() 
              : 'Summary not provided',
            answerAnalysis: aIndex !== -1 
              ? analysis.substring(aIndex + 2).trim() 
              : 'Answer analysis not provided'
          };

          console.log('Formatted analysis:', formattedAnalysis);

          try {
            // Create a new document reference first
            const newAnalysisRef = doc(collection(db, 'analyses'));
            const newAnalysisId = newAnalysisRef.id;

            const batch = writeBatch(db);
            
            // Set the analysis document with the known ID
            batch.set(newAnalysisRef, {
              userId: currentUser.uid,
              imageHash: imageHash,
              imageUrl: base64Image,
              analysis: formattedAnalysis,
              timestamp: serverTimestamp(),
              chatHistory: []
            });
            
            // Set the cache document
            const cacheRef = doc(collection(db, 'sharedAnalysisCache'));
            batch.set(cacheRef, {
              userId: currentUser.uid,
              imageHash: imageHash,
              analysis: formattedAnalysis,
              timestamp: serverTimestamp(),
            });

            await batch.commit();

            const newAnalysis = {
              id: newAnalysisId, // Use the known ID
              imageUrl: base64Image,
              analysis: formattedAnalysis,
              timestamp: new Date(),
              imageHash: imageHash,
              chatHistory: []
            };
            
            setResult(formattedAnalysis);
            setSelectedAnalysis(newAnalysis);
            setAnalysisHistory(prev => [newAnalysis, ...prev]);
            setLoading(false);
          } catch (err) {
            console.error('Error in Firestore batch write:', err);
            setError(`Failed to save analysis: ${err.message}`);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error processing image:', err);
          setError('Failed to process the image. Please try again.');
          setLoading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process the image. Please try again.');
      setLoading(false);
    }
  };

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      handleImageUpload(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1
  });

  const handlePaste = useCallback(async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          handleImageUpload(file);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // Chat message handler
  const handleSendMessage = async () => {
    if (!input.trim() || !result) return;

    const userMessage = input.trim();
    setInput('');
    
    const newChatHistory = [
      ...chatHistory,
      { role: 'user', content: userMessage }
    ];
    setChatHistory(newChatHistory);

    try {
      const response = await openai.chat.completions.create({
        model: "o1-preview",
        messages: [
          {
            role: "user",
            content: "As an expert tutor, help the student understand the question they're working on."
          },
          {
            role: "user",
            content: `Question text: ${result.extractedText}\n\nInitial analysis: ${result.summary}\n\nStudent question: ${userMessage}`
          }
        ],
        temperature: 1,
        max_completion_tokens: 1000
      });

      const updatedChatHistory = [
        ...newChatHistory,
        { role: 'assistant', content: response.choices[0].message.content }
      ];

      setChatHistory(updatedChatHistory);

      if (selectedAnalysis) {
        await updateAnalysisInFirestore(selectedAnalysis.id, { chatHistory: updatedChatHistory });
        
        const updatedAnalysis = {
          ...selectedAnalysis,
          chatHistory: updatedChatHistory
        };

        setSelectedAnalysis(updatedAnalysis);
        setAnalysisHistory(prev => 
          prev.map(item => 
            item.id === selectedAnalysis.id ? updatedAnalysis : item
          )
        );
      }

    } catch (err) {
      console.error('Error in chat:', err);
      setError('Failed to get response. Please try again.');
    }
  };

  const handleNewAnalysis = async () => {
    try {
      if (selectedAnalysis?.id && chatHistory.length > 0) {
        await updateAnalysisInFirestore(selectedAnalysis.id, { chatHistory });
        
        const updatedAnalysis = {
          ...selectedAnalysis,
          chatHistory: chatHistory
        };
        
        setAnalysisHistory(prev => 
          prev.map(item => 
            item.id === selectedAnalysis.id ? updatedAnalysis : item
          )
        );
      }

      setResult(null);
      setUploadedImage(null);
      setSelectedAnalysis(null);
      setChatHistory([]);
      setError(null);
      setInput('');
    } catch (err) {
      console.error('Error saving chat history:', err);
      setError('Failed to save chat history before starting new analysis.');
    }
  };

  return (
    <Grid container sx={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Grid 
        item 
        sx={{ 
          display: { xs: 'none', md: 'block' },
          width: '320px',
          height: '100vh',
          position: 'sticky',
          top: 0,
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        <AnalysisSidebar
          analysisHistory={analysisHistory}
          handleSelectAnalysis={handleSelectAnalysis}
          selectedAnalysis={selectedAnalysis}
          handleImageUpload={handleImageUpload}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          onNewAnalysis={handleNewAnalysis}
          isMobileView={isMobile}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
      </Grid>

      {/* Main Content */}
      <Grid 
        item 
        xs={12} 
        md 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Mobile Header */}
        {isMobile && (
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            bgcolor: theme.palette.background.paper,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              AI Analysis
            </Typography>
          </Box>
        )}

        {/* Loading and Error Overlays */}
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 3,
          }}>
            <Paper
              elevation={3}
              sx={{
                width: '90%',
                maxWidth: '600px',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <LoadingMessage />
            </Paper>
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{
              position: 'fixed', 
              top: { xs: 70, sm: 16 }, 
              left: '50%', 
              transform: 'translateX(-50%)',
              zIndex: 1000,
              width: { xs: '90%', sm: 'auto' },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Content Area */}
        <Box sx={{ 
          flex: 1,
          p: { xs: 2, sm: 3 },
          overflowY: 'auto',
          height: '100%',
          bgcolor: 'background.default',
        }}>
          <Box sx={{ 
            maxWidth: '800px', 
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
            {!result ? (
              <>
                <UploadArea
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  isDragActive={isDragActive}
                  handlePaste={handlePaste}
                />
                <Box sx={{ 
                  flex: 1,
                  minHeight: '600px',
                  maxHeight: '70vh',
                }}>
                  <ChatInterface
                    questionContext={{
                      currentAnalysisId: null,
                      chatHistory: []
                    }}
                    initialChatHistory={[]}
                    onChatUpdate={() => {}}
                  />
                </Box>
              </>
            ) : (
              <>
                <AnalysisResults
                  result={result}
                  uploadedImage={uploadedImage}
                  selectedAnalysis={selectedAnalysis}
                />
                <Box sx={{ 
                  flex: 1,
                  minHeight: '600px',
                  maxHeight: '70vh',
                }}>
                  <ChatInterface
                    questionContext={{
                      questionType: result?.questionType,
                      summary: result?.summary,
                      answerAnalysis: result?.answerAnalysis,
                      currentAnalysisId: selectedAnalysis?.id,
                      chatHistory: selectedAnalysis?.chatHistory || []
                    }}
                    initialChatHistory={selectedAnalysis?.chatHistory || []}
                    onChatUpdate={async (newChatHistory) => {
                      if (!selectedAnalysis?.id) return;
                      try {
                        await updateAnalysisInFirestore(selectedAnalysis.id, { chatHistory: newChatHistory });
                        const updatedAnalysis = {
                          ...selectedAnalysis,
                          chatHistory: newChatHistory
                        };
                        setSelectedAnalysis(updatedAnalysis);
                        setChatHistory(newChatHistory);
                        setAnalysisHistory(prev => 
                          prev.map(item => 
                            item.id === selectedAnalysis.id ? updatedAnalysis : item
                          )
                        );
                      } catch (err) {
                        console.error('Error updating chat history:', err);
                        setError('Failed to save chat history. Please try again.');
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '320px',
              background: theme.palette.mode === 'dark'
                ? 'rgba(15, 23, 42, 0.8)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.5)'
                : '0 4px 12px rgba(0,0,0,0.1)',
            }
          }}
        >
          <AnalysisSidebar
            analysisHistory={analysisHistory}
            handleSelectAnalysis={handleSelectAnalysis}
            selectedAnalysis={selectedAnalysis}
            handleImageUpload={handleImageUpload}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            onNewAnalysis={handleNewAnalysis}
            isMobileView={isMobile}
            isOpen={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          />
        </Drawer>

        {/* Mobile Actions */}
        {isMobile && (
          <Box sx={{ 
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            zIndex: 1100,
          }}>
            {/* Previous Chat Button */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 2,
                width: 48,
                height: 48,
                '&:hover': {
                  bgcolor: 'background.paper',
                },
              }}
            >
              <History />
            </IconButton>
            
            {/* New Analysis Button */}
            <IconButton
              onClick={handleNewAnalysis}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 2,
                width: 48,
                height: 48,
                '&:hover': {
                  bgcolor: 'background.paper',
                },
              }}
            >
              <Add />
            </IconButton>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default AIAnalysis; 